import { action, observable } from 'mobx';
import { LoggedUser, ClientData, Advisor, UserAccount } from '../common/user';
import { RootStore } from './rootStore';
import { UserRoles, Client, AccountStatus } from 'enums';
import { APP_CONFIG } from 'appConfig';
import Keycloak from 'keycloak-js';
import { permissions, Permission, RequiredPermissions, ArrayCheck } from 'helpers/permissions';
import { merge } from 'lodash';
import {
  API_CLIENT_DATA,
  API_CLIENT_DATA_SIGN,
  API_MAX_LOGIN_LENGTH,
  ADMIN_TRADING_DAY,
  ADVISOR_TRADING_DAY,
  ADVISOR_LOGO,
} from 'api/endpoints';
import { idp } from 'helpers/realms';

import { ExtProdSortBy } from 'pages/Accounts/Edit/components/ExternalProducts/model';

const storageKey = 'viewSettings';
export const maxLoginLengthKey = 'maxLoginTime';

export interface LogoData {
  custom: boolean;
  enabled: boolean;
  data: string;
}

export enum ViewKeys {
  DEMO_MODE = 'demoMode',
  HIDE_MESSAGES = 'hideMessages',
  HIDE_LOGIN_EXPIRING = 'hideLoginExpiring',
  EXTERNAL_PRODUCTS = 'externalProducts'
}

interface ViewSettings {
  demoMode: boolean;
  hideMessages: string[];
  hideLoginExpiring: boolean;
  externalProducts: {
    sortBy: ExtProdSortBy;
    sortDir: boolean;
  }
}

const defaultView: { [key in ViewKeys]: string | boolean | string[] | any } = {
  demoMode: false,
  hideMessages: [],
  hideLoginExpiring: false,
  externalProducts: {
    sortBy: ExtProdSortBy.TITLE,
    sortDir: true
  }
};

interface MetaData {
  tradingDay?: string;
}

class UserStoreKC {
  private rootStore: RootStore;
  private keycloak: Keycloak;
  private loginExpiringTimerID: number;
  private sessionExpiringTimerID: number;
  private sessionExpiredTimerID: number;
  private sessionLength: number;

  constructor(rootStore: RootStore, keycloak: Keycloak) {
    this.rootStore = rootStore;
    this.keycloak = keycloak;
    this.loginExpiringTimerID = 0;
    this.sessionExpiringTimerID = 0;
    this.sessionExpiredTimerID = 0;
    this.sessionLength = 0;
    this.metadata = {};
  }

  @observable loginExpiring = false;
  @observable sessionExpiring = false;
  @observable sessionExpired = false;
  @observable loggedUser?: LoggedUser;
  @observable clientData?: ClientData;
  @observable activeAccount?: UserAccount = undefined;
  @observable clientError: string | boolean = false;
  @observable loading = true;
  @observable metadata: MetaData;

  getViewSettings = () => {
    return localStorage.getItem(storageKey)
      ? (merge({}, defaultView, JSON.parse(localStorage.getItem(storageKey)!) as ViewSettings))
      : defaultView;
  };

  saveViewSettings = () => {
    localStorage.setItem(storageKey, JSON.stringify(this.view));
  };

  @observable view = this.getViewSettings();

  @action
  setViewSetting = (key: ViewKeys, value: string | boolean | string[] | any) => {
    this.view[key] = value;
    this.saveViewSettings();
  };

  @action
  addToViewSetting = (key: ViewKeys, value: string) => {
    if (Array.isArray(this.view[key])) {
      const a = this.view[key] as string[];
      a.push(value);
      this.saveViewSettings();
    }
  };

  @action
  removeFromViewSetting = (key: ViewKeys, value: string) => {
    if (Array.isArray(this.view[key])) {
      const a = this.view[key] as string[];
      const i = a.indexOf(value);
      if (i !== -1) {
        a.splice(i, 1);
      }
      this.saveViewSettings();
    }
  };

  inViewSetting = (key: ViewKeys, value: string) => {
    if (Array.isArray(this.view[key])) {
      const a = this.view[key] as string[];
      return a.indexOf(value) > -1;
    } else {
      return false;
    }
  };

  @action setSessionExpiring = (sessionExpiring: boolean) => (this.sessionExpiring = sessionExpiring);
  @action setLoginExpiring = () => (this.loginExpiring = true);

  @action setSessionExpired = (sessionExpired: boolean) => {
    this.sessionExpired = sessionExpired;
    if (sessionExpired) this.sessionExpiring = false;
  };

  @action setLoggedUser = (loggedUser: LoggedUser) => (this.loggedUser = loggedUser);

  @action setActiveAccount = (account: UserAccount) => (this.activeAccount = account);

  @action
  clearTimers = (cleanup?: boolean) => {
    this.sessionExpiringTimerID && window.clearTimeout(this.sessionExpiringTimerID);
    this.sessionExpiredTimerID && window.clearTimeout(this.sessionExpiredTimerID);
    cleanup && this.loginExpiringTimerID && window.clearTimeout(this.loginExpiringTimerID);
  };

  @action
  getToken = () => this.keycloak.token || '';

  accountManagement(): void {
    const baseUrl: string = this.keycloak.createAccountUrl();
    const url = new URL(baseUrl);
    url.pathname = url.pathname.replace('/account', '/account/account-security');
    const targetPath: string = idp.includes(this.loggedUser?.realm ?? '') ? '/device-activity' : '/signing-in';
    url.pathname += targetPath;
    window.location.href = url.toString();
  }

  clearMaxLoginTime = () => {
    localStorage.removeItem(maxLoginLengthKey);
  };

  @action
  fetchAdvisorLogo = async () => {
    const logo = await this.rootStore.stores.apiStore.get<LogoData>(ADVISOR_LOGO);
    if (logo?.data?.data) {
      this.loggedUser!.logo = logo.data.data;
    } else {
      this.loggedUser!.logo = logo?.data?.enabled === false ? null : undefined;
    }
  };

  @action
  async getUserInfo() {
    this.setLoggedUser({
      firstname: this.keycloak.idTokenParsed ? this.keycloak.idTokenParsed.given_name || ' ' : ' ',
      lastname: this.keycloak.idTokenParsed ? this.keycloak.idTokenParsed.family_name || ' ' : ' ',
      realm: this.keycloak.realm || '',
      roles:
        (this.keycloak.tokenParsed?.resource_access?.backend?.roles as UserRoles[]) ||
        (this.keycloak.tokenParsed?.realm_access?.roles as UserRoles[]) ||
        [],
      client: this.keycloak.tokenParsed?.azp as Client,
      logo: undefined,
    });

    this.sessionLength = APP_CONFIG.sessionExpiredTimeout * 1000;
    this.resetSessionTimers();

    const currentTime = Math.floor(new Date().getTime() / 1000);

    if (!localStorage.getItem(maxLoginLengthKey)) {
      const response = await this.rootStore.stores.apiStore.get<{ value: number }>(API_MAX_LOGIN_LENGTH);
      if (response?.data) {
        localStorage.setItem(maxLoginLengthKey, (currentTime + response.data.value).toString());
      }
    }
    const delta =
      Number(localStorage.getItem(maxLoginLengthKey) || 0) - currentTime - (APP_CONFIG.loginExpiringTimeout || 0); // in seconds
    if (!this.view.hideLoginExpiring) {
      this.loginExpiringTimerID = window.setTimeout(() => this.setLoginExpiring(), Math.max(delta * 1000, 1000));
    }

    if (this.checkPermissions(Permission.APP_ENDUSER)) {
      await this.fetchClientData();
    } else {
      this.rootStore.stores.userStore.checkPermissions(Permission.ACCESS_ADVISOR) && this.fetchAdvisorLogo();
      this.loading = false;
      const response = await this.rootStore.stores.apiStore.get<{ value: string }>(
        this.rootStore.stores.userStore.checkPermissions(Permission.ACCESS_BACKOFFICE)
          ? ADMIN_TRADING_DAY
          : ADVISOR_TRADING_DAY,
      );
      if (response?.data) {
        this.metadata.tradingDay = response.data.value;
      }
    }

    if (APP_CONFIG.kcUserinfo) {
      /* eslint-disable */
      console.log('ID');
      console.log(this.keycloak.idToken);
      console.log(this.keycloak.idTokenParsed);
      console.log('ACCESS');
      console.log(this.keycloak.token);
      console.log(this.keycloak.tokenParsed);
      console.log('REFRESH');
      console.log(this.keycloak.refreshToken);
      console.log(this.keycloak.refreshTokenParsed);
      console.log('ROLES');
      console.log(JSON.stringify(this.loggedUser?.roles));
      /* eslint-enable */
    }
  }

  @action
  resetSessionTimers = () => {
    this.clearTimers();

    this.sessionExpiredTimerID = window.setTimeout(() => {
      this.setSessionExpired(true);
      this.logout();
    }, this.sessionLength);

    this.sessionExpiringTimerID = window.setTimeout(
      () => this.setSessionExpiring(true),
      this.sessionLength - (APP_CONFIG.sessionExpiringTimeout || 0) * 1000,
    );

    this.sessionExpiring = false;
    this.sessionExpired = false;
  };

  @action
  cleanup = () => {
    localStorage.removeItem(storageKey);
    localStorage.removeItem('services');
    sessionStorage.clear();
    this.clearTimers(true);
  };

  @action
  logout = async (redirectUri?: string) => {
    this.cleanup();
    this.rootStore.stores.uiStore.sendLogoutToNotes();
    this.keycloak.logout(redirectUri ? { redirectUri } : undefined);
  };

  @action
  async fetchClientData() {
    this.rootStore.stores.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.get<ClientData>(
      this.loggedUser?.client === Client.CLIENT_PORTAL ? API_CLIENT_DATA : API_CLIENT_DATA_SIGN,
    );
    this.rootStore.stores.uiStore.setLoading(false);
    if (response?.data) {
      this.clientData = response.data;
      this.clientError = false;
    } else if (response?.status === 403) {
      this.clientError = 'client_error_unauth';
    } else {
      this.clientError = true;
    }
    this.loading = false;
  }

  validateVs = (vs: string) => {
    return this.clientData && vs && this.clientData.accounts.findIndex((a) => a.variableSymbol === vs) > -1
      ? vs
      : this.activeAccountVs();
  };

  validateAccountId = (accountId: string) => {
    return this.clientData && accountId && this.clientData.accounts.findIndex((a) => a.accountId === accountId) > -1
      ? accountId
      : this.activeAccount?.accountId;
  };

  activeAccountVs = () => {
    return this.clientData?.accounts.find((a) => a.accountId === this.activeAccount?.accountId)?.variableSymbol;
  };

  activeAccountName = () => {
    return this.clientData?.accounts.find((a) => a.accountId === this.activeAccount?.accountId)?.title;
  };

  @action
  accountIdFromVs = (vs?: string, accId?: string) => {
    if (!this.clientData || this.clientData.accounts.length === 0) return undefined;

    const found = this.clientData.accounts.find(
      (a) => (vs && a.variableSymbol === vs) || (accId && a.accountId === accId),
    );

    if (found) {
      this.setActiveAccount(found);
    } else {
      const showFirst = this.clientData.accounts.find(
        (a) => a.status === AccountStatus.TO_BE_SIGNED || a.pendingSignature || a.pendingRequestSignature,
      );
      this.setActiveAccount(showFirst || this.clientData.accounts[0]);
    }
  };

  advisorData = (): Advisor | undefined => {
    if (this.clientData && this.activeAccount?.accountId) {
      const activeAccount = this.clientData.accounts.find((a) => a.accountId === this.activeAccount?.accountId);
      return activeAccount?.advisor || undefined;
    }
    return undefined;
  };

  hasRoles = (roles?: UserRoles | UserRoles[], check?: ArrayCheck) => {
    return this.loggedUser ? this.hasPermissions<UserRoles>(this.loggedUser.roles, roles, check) : false;
  };

  hasPermissions = <T>(userInfo: T[], permissions?: T | T[], check?: ArrayCheck) => {
    if (!permissions) return true;
    if (Array.isArray(permissions)) {
      if (!check || check === ArrayCheck.ANY) {
        return permissions.some((i) => userInfo.includes(i));
      } else {
        return permissions.every((i) => userInfo.includes(i));
      }
    } else {
      return userInfo.includes(permissions);
    }
  };

  checkPermissions = (name: Permission, id?: string) => {
    if (!this.loggedUser) return false;
    const requirement = id
      ? (permissions[name] as { [key: string]: RequiredPermissions })[id]
      : (permissions[name] as RequiredPermissions);
    if (requirement === undefined) return false;

    return !(requirement.roles && !this.hasRoles(requirement.roles, requirement.rolesCheck));
  };

  checkSignature = (signature?: boolean) => {
    return (
      !signature ||
      this.activeAccount?.status === AccountStatus.TO_BE_SIGNED ||
      this.activeAccount?.pendingSignature ||
      this.activeAccount?.pendingRequestSignature
    );
  };
}

export { UserStoreKC };
